export const INTERSECT_SIGN_WIDTH = 2500;
export const INTERSECT_SIGN_HEIGHT = 1144;
export const INTERSECT_SIGN_MAX_BYTES = 4 * 1024 * 1024;
export type IntersectSign = { url: string; version: number };
export type SignFit = 'contain' | 'cover' | 'fill';
export const SIGN_BACKGROUND = '#1f2937';

export function parseIntersectSign(value: unknown): IntersectSign {
  const sign = value as IntersectSign;
  if (!sign || typeof sign.url !== 'string' || !Number.isSafeInteger(sign.version) || sign.version < 0)
    throw new Error('Invalid IntersectSign response.');
  if (sign.url) {
    const url = new URL(sign.url);
    if (url.protocol !== 'https:' || url.username || url.password) throw new Error('Invalid IntersectSign URL.');
  }
  return sign;
}

export async function validateIntersectSignFile(file: File): Promise<void> {
  if (!['image/png', 'image/jpeg'].includes(file.type)) throw new Error('Choose a PNG or JPEG image.');
  if (!file.size || file.size > INTERSECT_SIGN_MAX_BYTES) throw new Error('Image must be at most 4 MiB.');
  const url = URL.createObjectURL(file);
  try {
    await new Promise<void>((resolve, reject) => {
      const image = new Image();
      image.onload = () => image.naturalWidth > 0 && image.naturalHeight > 0 && image.naturalWidth * image.naturalHeight <= 32000000
        ? resolve() : reject(new Error('Image must have valid dimensions and be at most 32 megapixels.'));
      image.onerror = () => reject(new Error('This image could not be decoded.'));
      image.src = url;
    });
  } finally { URL.revokeObjectURL(url); }
}

export function signImagePlacement(width: number, height: number, fit: SignFit) {
  const scale = (fit === 'cover' ? Math.max : Math.min)(INTERSECT_SIGN_WIDTH / width, INTERSECT_SIGN_HEIGHT / height);
  const w = fit === 'fill' ? INTERSECT_SIGN_WIDTH : width * scale;
  const h = fit === 'fill' ? INTERSECT_SIGN_HEIGHT : height * scale;
  return { height: h, width: w, x: (INTERSECT_SIGN_WIDTH - w) / 2, y: (INTERSECT_SIGN_HEIGHT - h) / 2 };
}

// Bake the chosen framing into the existing texture contract. Every viewer,
// including older Unity clients, sees the same framing without resizing the mesh.
export async function prepareIntersectSignFile(file: File, fit: SignFit): Promise<File> {
  await validateIntersectSignFile(file);
  const url = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const decoded = new Image();
      decoded.onload = () => resolve(decoded);
      decoded.onerror = () => reject(new Error('This image could not be decoded.'));
      decoded.src = url;
    });
    const canvas = document.createElement('canvas');
    canvas.width = INTERSECT_SIGN_WIDTH; canvas.height = INTERSECT_SIGN_HEIGHT;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Image processing is unavailable in this browser.');
    context.fillStyle = SIGN_BACKGROUND;
    context.fillRect(0, 0, canvas.width, canvas.height);
    const placement = signImagePlacement(image.naturalWidth, image.naturalHeight, fit);
    context.drawImage(image, placement.x, placement.y, placement.width, placement.height);
    for (const quality of [0.94, 0.85, 0.75]) {
      const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= INTERSECT_SIGN_MAX_BYTES)
        return new File([blob], 'intersect-sign.jpg', { type: 'image/jpeg' });
    }
    throw new Error('The processed image exceeds 4 MiB. Please choose a simpler image.');
  } finally { URL.revokeObjectURL(url); }
}
